import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { AlertCircle, Pencil, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { ContractInstallations } from "@/components/contracts/ContractInstallations";
import { AddInstallationDialog } from "@/components/contracts/AddInstallationDialog";
import { EditContractDialog } from "@/components/contracts/EditContractDialog";
import { ContractBillingWorkspace } from "@/components/contracts/ContractBillingWorkspace";
import { ContractPartiesSection } from "@/components/contracts/ContractPartiesSection";
import { ContractDocumentsPanel } from "@/components/contracts/ContractDocumentsPanel";
import { CertificationsSection } from "@/components/contracts/certifications/CertificationsSection";
import { useContractInstallationIntent } from "@/lib/contractInstallationIntent";
import { contractTypeLabel } from "@/lib/contractLabels";
import {
  applyContractDetailTabToSearch,
  classifyContractDetailError,
  getContractDetailReturnUrl,
  getContractDetailTabFromSearch,
  parseContractDetailTab,
  type ContractDetailTab,
} from "@/lib/contractDetailErrors";
import { removeCertificationDeepLinkFromSearch } from "@/lib/certificationDeepLink";

const STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  awarded: "Adjudicado",
  pending_signature: "Pendiente firma",
  signed: "Firmado",
  active: "Activo",
  completed: "Completado",
  cancelled: "Cancelado",
};

const ACTION_LABELS: Record<string, string> = {
  awarded: "Adjudicar contrato",
  pending_signature: "Enviar a firma",
  signed: "Marcar como firmado",
  active: "Activar contrato",
  completed: "Completar contrato",
  cancelled: "Cancelar contrato",
};

function translateContractTransitionError(msg: string): string {
  if (msg.includes("CONTRACT_STALE_UPDATE")) return "El contrato ha sido modificado por otro usuario. Recarga la página e inténtalo de nuevo.";
  if (msg.includes("CONTRACT_CANCEL_REASON_REQUIRED")) return "Debes indicar un motivo de cancelación (mínimo 3 caracteres).";
  if (msg.includes("CONTRACT_MISSING_CONTRACTING_PARTY")) return "No se puede cambiar el estado: el contrato no tiene una parte contratante principal.";
  if (msg.includes("CONTRACT_MISSING_BILL_TO")) return "No se puede cambiar el estado: el contrato no tiene una entidad a facturar asignada.";
  if (msg.includes("CONTRACT_BILLING_PARTNER_MISMATCH")) return "No se puede cambiar el estado: la entidad a facturar no coincide con la configurada en el contrato.";
  if (msg.includes("CONTRACT_BILLING_MODE_REQUIRED")) return "No se puede cambiar el estado: debes definir el modo de facturación antes de firmar/activar.";
  if (msg.includes("CONTRACT_AMOUNT_REQUIRED_FOR_BILLING")) return "No se puede cambiar el estado: se requiere un importe original mayor a cero para un contrato facturable.";
  if (msg.includes("CONTRACT_INVALID_TRANSITION")) return "Transición de estado no permitida para el estado actual.";
  if (msg.includes("CONTRACT_TERMINAL_STATE")) return "El contrato ya se encuentra en un estado terminal.";
  if (msg.includes("CONTRACT_ARCHIVED")) return "El contrato está archivado y no permite cambios de estado.";
  return msg || "Error al cambiar el estado del contrato.";
}

const BILLING_STRATEGY_LABELS: Record<string, string> = {
  milestones: "Hitos",
  periodic_certifications: "Certificaciones periódicas",
  mixed: "Mixta",
  single_invoice: "Factura única",
  on_demand: "A demanda",
  non_billable: "No facturable",
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  transfer: "Transferencia",
  confirming: "Confirming",
  promissory_note: "Pagaré",
  cash: "Efectivo",
  other: "Otro",
};

const PAYMENT_TERM_BASIS_LABELS: Record<string, string> = {
  invoice_date: "Fecha de factura",
  end_of_month: "Fin de mes",
  client_acceptance: "Aceptación del cliente",
  other: "Otra condición",
};

const BILLING_CHANNEL_LABELS: Record<string, string> = {
  email: "Email",
  face: "FACe",
  portal: "Portal",
  other: "Otro",
};

function fmtMoney(v: string | number | null | undefined, currency = "EUR"): string {
  if (v == null || v === "") return "—";
  const amount = Number(v);
  return Number.isFinite(amount)
    ? amount.toLocaleString("es-ES", { style: "currency", currency })
    : "—";
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toISOString().split("T")[0];
}

export default function ContractDetailPage() {
  const [matchesContractRoute, params] = useRoute("/customers/:customerPartnerId/contracts/:contractId");
  const [, setLocation] = useLocation();
  const contractId = params?.contractId ? Number(params.contractId) : null;
  const routeCustomerPartnerId = params?.customerPartnerId ? Number(params.customerPartnerId) : null;
  const [activeTab, setActiveTab] = useState<ContractDetailTab>(() =>
    getContractDetailTabFromSearch(window.location.search)
  );
  const [installationDialogContractId, setInstallationDialogContractId] = useState<number | null>(null);
  const addInstallationOpen = contractId != null && installationDialogContractId === contractId;
  const setAddInstallationOpen = (open: boolean) => setInstallationDialogContractId(open ? contractId : null);
  const [createdContractContext, setCreatedContractContext] = useState<number | null>(null);
  const installationRoute = `${routeCustomerPartnerId}/${contractId}`;
  const previousInstallationRoute = useRef(installationRoute);
  useEffect(() => {
    if (previousInstallationRoute.current !== installationRoute) {
      previousInstallationRoute.current = installationRoute;
      setInstallationDialogContractId(null);
      setCreatedContractContext(null);
    }
  }, [installationRoute]);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  // Deep-link intent from the contracts list: ?edit=1 opens the draft editor.
  const initialEditIntent = (() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("edit") === "1";
  })();
  const utils = trpc.useUtils();

  // A route-pattern miss is a navigation transition, not a malformed contract URL.
  // Only reject IDs when this component still matches its contract route.
  const hasValidRouteIds =
    typeof contractId === "number" &&
    Number.isSafeInteger(contractId) &&
    contractId > 0 &&
    typeof routeCustomerPartnerId === "number" &&
    Number.isSafeInteger(routeCustomerPartnerId) &&
    routeCustomerPartnerId > 0;
  const isInvalidRoute = matchesContractRoute && !hasValidRouteIds;
  const isValidRoute = matchesContractRoute && hasValidRouteIds;

  useEffect(() => {
    if (isInvalidRoute) setLocation("/404", { replace: true });
  }, [isInvalidRoute, setLocation]);

  const capsQuery = trpc.customers.contracts.getMyCapabilities.useQuery(undefined, {
    enabled: isValidRoute,
  });
  const caps = capsQuery.data;

  // PERM-01 (P0-B): the Certificaciones tab is reachable only by roles whose
  // certification caps include canRead (fail-closed server RBAC). It grants no
  // contract economics: canReadEconomics/canWriteEconomic keep coming solely
  // from getMyCapabilities, which denies them to project_manager.
  const certCapsQuery = trpc.contractCertificationsGetMyCapabilities.useQuery(undefined, {
    enabled: isValidRoute,
  });
  const canReadCertifications = certCapsQuery.data?.canRead ?? false;

  const query = trpc.customers.contracts.get.useQuery(
    { contractId: contractId ?? 0 },
    { enabled: isValidRoute },
  );
  const c = query.data;

  useContractInstallationIntent({
    routeCustomerId: routeCustomerPartnerId,
    routeContractId: contractId,
    contract: c,
    canLinkInstallations: caps?.canLinkInstallations,
    failed: query.isError || capsQuery.isError,
  }, () => {
    setCreatedContractContext(contractId);
    setActiveTab("instalaciones");
    setAddInstallationOpen(true);
  });

  // Route integrity belongs to navigation state, not render. This keeps
  // browser history valid and avoids a render-time redirect loop. The current
  // query string (?tab=… and other params) is preserved across the fix-up.
  useEffect(() => {
    // Cached query data must not canonicalize an invalid route over its 404.
    if (
      isValidRoute &&
      c &&
      routeCustomerPartnerId != null &&
      c.customerPartnerId !== routeCustomerPartnerId
    ) {
      setLocation(`/customers/${c.customerPartnerId}/contracts/${c.id}${window.location.search}`, { replace: true });
    }
  }, [c, isValidRoute, routeCustomerPartnerId, setLocation]);

  // NAV-01 (P0-B): the visible tab mirrors the ?tab= query parameter. Tab
  // clicks push a new history entry (so browser back restores the previous
  // tab), and popstate (back/forward) re-reads the tab from the URL.
  const handleTabChange = (tabValue: string) => {
    const nextTab = parseContractDetailTab(tabValue);
    setActiveTab(nextTab);
    const params = new URLSearchParams(window.location.search);
    if (nextTab !== "certificaciones") params.delete("certificationId");
    const search = applyContractDetailTabToSearch(params.toString(), nextTab);
    if (c) {
      setLocation(`/customers/${c.customerPartnerId}/contracts/${c.id}${search}`);
    } else {
      setLocation(`${window.location.pathname}${search}`);
    }
  };

  useEffect(() => {
    const syncTabFromUrl = () => {
      setActiveTab(getContractDetailTabFromSearch(window.location.search));
    };
    window.addEventListener("popstate", syncTabFromUrl);
    return () => window.removeEventListener("popstate", syncTabFromUrl);
  }, []);

  const canChangeStatus = caps?.canChangeStatus ?? false;
  const canWriteCommercial = caps?.canWriteCommercial ?? false;

  // The visible "Editar contrato" action is draft-only: for any other status
  // the button is not rendered at all (the server re-enforces draft-only).
  const canEditDraft = c != null && c.status === "draft" && canWriteCommercial;

  useEffect(() => {
    if (initialEditIntent && canEditDraft) {
      setEditOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete("edit");
      window.history.replaceState({}, "", url.toString());
    }
  }, [initialEditIntent, canEditDraft]);

  // A deep link to ?tab=certificaciones from a role without certification read
  // access falls back to the summary tab instead of showing an empty shell.
  useEffect(() => {
    if (
      isValidRoute &&
      !certCapsQuery.isLoading &&
      !canReadCertifications &&
      activeTab === "certificaciones"
    ) {
      setActiveTab("resumen");
      const nextSearch = removeCertificationDeepLinkFromSearch(
        applyContractDetailTabToSearch(window.location.search, "resumen"),
      );
      setLocation(`${window.location.pathname}${nextSearch}`, { replace: true });
    }
  }, [isValidRoute, certCapsQuery.isLoading, canReadCertifications, activeTab, setLocation]);

  const availableTransitionsQuery = trpc.customers.contracts.getAvailableTransitions.useQuery(
    { currentStatus: c?.status ?? "" },
    { enabled: isValidRoute && !!c?.status && canChangeStatus },
  );
  const availableTransitions = (availableTransitionsQuery.data ?? []) as string[];
  const transitionMut = trpc.customers.contracts.transitionStatus.useMutation();

  if (!isValidRoute) {
    return null;
  }

  if (query.isLoading || capsQuery.isLoading || certCapsQuery.isLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-3">
          <Skeleton className="h-12 w-72" />
          <Skeleton className="h-40 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (query.isError || !c) {
    const errorInfo = classifyContractDetailError(query.error);
    const returnUrl = getContractDetailReturnUrl(routeCustomerPartnerId);

    // NAV-01 (P0-B): a missing contract gets an explicit, user-facing 404
    // state instead of a generic error shell. Forbidden/internal failures
    // keep their own (distinct, safe) handling below.
    if (errorInfo.kind === "NOT_FOUND") {
      return (
        <DashboardLayout>
          <Card className="max-w-xl mx-auto my-8" data-testid="contract-not-found">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <span>404 — Contrato no encontrado</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">{errorInfo.message}</p>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button asChild variant="outline" size="sm">
                  <Link
                    href={returnUrl}
                    aria-label="Volver al cliente"
                    data-testid="volver-al-cliente"
                  >
                    ← Volver al cliente
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <Card className="max-w-xl mx-auto my-8">
          <CardHeader>
            <CardTitle className="text-base text-destructive flex items-center gap-2">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>Error al cargar el contrato</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{errorInfo.message}</p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              {errorInfo.canRetry && (
                <Button variant="default" size="sm" onClick={() => query.refetch()}>
                  Reintentar
                </Button>
              )}
              <Button asChild variant="outline" size="sm">
                <Link
                  href={returnUrl}
                  aria-label="Volver al cliente"
                  data-testid="volver-al-cliente"
                >
                  Volver al cliente
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  // Route integrity: customerPartnerId in URL must match contract's customerPartnerId
  if (routeCustomerPartnerId != null && c.customerPartnerId !== routeCustomerPartnerId) {
    return null;
  }

  const canReadEconomics = caps?.canReadEconomics ?? false;
  const canWriteEconomic = caps?.canWriteEconomic ?? false;
  const canLinkInstallations = caps?.canLinkInstallations ?? false;

  const isTerminalState = c.status === "completed" || c.status === "cancelled";
  const showTransitionActions = canChangeStatus && !isTerminalState && availableTransitions.length > 0;

  async function handleTransition(targetStatus: string, reason?: string) {
    if (!c) return;
    try {
      await transitionMut.mutateAsync({
        contractId: c.id,
        toStatus: targetStatus as any,
        reason: reason ? reason.trim() : undefined,
        expectedUpdatedAt: new Date(c.updatedAt).toISOString(),
      });
      toast.success("Estado del contrato actualizado.");
      setCancelModalOpen(false);
      setCancellationReason("");
      utils.customers.contracts.get.invalidate({ contractId: c.id });
      utils.customers.contracts.getAvailableTransitions.invalidate({ currentStatus: c.status });
    } catch (err: any) {
      toast.error(translateContractTransitionError(err?.message ?? ""));
    }
  }

  // Customer display: use partner roles to find contracting_party, or fall back
  // to customerCode if present. Avoid displaying raw internal IDs (mandate §4.5).
  const contractingPartyRole = (c.partnerRoles ?? []).find(
    (r: { role: string; isPrimaryForRole: boolean }) => r.role === "contracting_party" && r.isPrimaryForRole
  ) as { partnerCode?: string; legalName?: string } | undefined;
  const customerDisplay = contractingPartyRole
    ? `${contractingPartyRole.partnerCode ? `[${contractingPartyRole.partnerCode}] ` : ""}${contractingPartyRole.legalName ?? ""}`
    : "Cliente sin denominación";
  const billingPartnerDisplay = c.billingPartnerName
    ? `${c.billingPartnerCode ? `[${c.billingPartnerCode}] ` : ""}${c.billingPartnerName}`
    : "Destinatario de facturación pendiente";
  const billToRole = (c.partnerRoles ?? []).find(
    (r: { role: string; isPrimaryForRole: boolean; finalizedAt?: string | null; validUntil?: string | null }) =>
      r.role === "bill_to" && r.isPrimaryForRole && !r.finalizedAt && !r.validUntil,
  ) as { businessPartnerId: number; partnerCode: string; legalName: string } | undefined;
  const billToOption = billToRole
    ? { id: billToRole.businessPartnerId, partnerCode: billToRole.partnerCode, legalName: billToRole.legalName }
    : c.billingPartnerId != null && c.billingPartnerName
      ? { id: c.billingPartnerId, partnerCode: c.billingPartnerCode ?? "", legalName: c.billingPartnerName }
      : null;

  return (
    <DashboardLayout>
      <div className="min-w-0 max-w-full space-y-4">
        {createdContractContext === contractId && (
          <p role="status" className="rounded-md border p-3 text-sm break-words">
            El contrato {c.contractCode} ya está creado. Cerrar o cancelar el alta de una instalación no elimina el contrato.
            Puedes continuar con «Añadir instalación» sin volver a crearlo.
          </p>
        )}
        <div className="flex min-w-0 max-w-full flex-wrap items-start justify-between gap-3">
          {/* Reserve a readable title width before wrapping actions onto a new row. */}
          <div className="min-w-0 max-w-full flex-[1_1_24rem] [overflow-wrap:anywhere]">
            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
              <h1 className="min-w-0 max-w-full text-xl font-semibold [overflow-wrap:anywhere]">
                {c.contractCode} — {c.title}
              </h1>
              <Badge variant={c.status === "cancelled" ? "destructive" : c.status === "completed" ? "secondary" : "default"}>
                {STATUS_LABELS[c.status] ?? "Estado registrado"}
              </Badge>
            </div>
            <p className="min-w-0 max-w-full text-sm text-muted-foreground [overflow-wrap:anywhere]">
              {customerDisplay}
              {c.salesOwnerName ? ` · Comercial: ${c.salesOwnerName}` : ""}
              {c.projectManagerName ? ` · PM: ${c.projectManagerName}` : ""}
            </p>
          </div>
          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
            {canEditDraft && (
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil className="w-4 h-4 mr-1" />
                Editar contrato
              </Button>
            )}
            {showTransitionActions && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" disabled={transitionMut.isPending}>
                    Cambiar estado…
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {availableTransitions.map((target) => (
                    <DropdownMenuItem
                      key={target}
                      onClick={() => {
                        if (target === "cancelled") {
                          setCancelModalOpen(true);
                        } else {
                          handleTransition(target);
                        }
                      }}
                      className={target === "cancelled" ? "text-destructive font-medium" : ""}
                    >
                      {ACTION_LABELS[target] ?? `Cambiar a ${STATUS_LABELS[target] ?? target}`}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button asChild variant="outline" size="sm">
              <Link
                href={getContractDetailReturnUrl(routeCustomerPartnerId)}
                aria-label="Volver al cliente"
                data-testid="volver-al-cliente"
              >
                ← Volver al cliente
              </Link>
            </Button>
          </div>
        </div>
        {c.economics?.certificationEconomicReviewRequired && (
          <p role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
            {c.economics.certificationEconomicReviewMessage ?? "Hay certificaciones con revisión económica pendiente. Los compromisos y el disponible no son autoritativos."}
          </p>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="resumen">Resumen</TabsTrigger>
            <TabsTrigger value="instalaciones">Instalaciones</TabsTrigger>
            <TabsTrigger value="economia">Economía</TabsTrigger>
            {canReadCertifications && <TabsTrigger value="certificaciones">Certificaciones</TabsTrigger>}
            <TabsTrigger value="documentos">Documentos</TabsTrigger>
            <TabsTrigger value="partes">Partes</TabsTrigger>
          </TabsList>

          <TabsContent value="resumen" className="space-y-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Resumen del contrato</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2 text-sm sm:grid-cols-2">
                <span>Código: {c.contractCode}</span>
                <span>Tipo: {contractTypeLabel(c.contractType)}</span>
                <span>Adjudicación: {fmtDate(c.awardDate)}</span>
                <span>Firma: {fmtDate(c.signedDate)}</span>
                <span>Inicio: {fmtDate(c.contractualStartDate)}</span>
                <span>Fin: {fmtDate(c.contractualEndDate)}</span>
                <span>Mantenimiento: {c.includesMaintenance ? "Sí" : "No"}</span>
                <span className="sm:col-span-2">
                  Facturar a: {billingPartnerDisplay} · Canal: {c.billingChannel ? BILLING_CHANNEL_LABELS[c.billingChannel] ?? c.billingChannel : "Pendiente de configurar"}
                </span>
                {c.economics ? (
                  <>
                    <span>Estrategia de facturación: {BILLING_STRATEGY_LABELS[c.economics.billingStrategy ?? ""] ?? "Pendiente de configurar"}</span>
                    <span>Método de pago: {PAYMENT_METHOD_LABELS[c.economics.paymentMethod ?? ""] ?? "Pendiente de configurar"}</span>
                    <span>Base/plazo: {c.economics.paymentTermBasis ? `${PAYMENT_TERM_BASIS_LABELS[c.economics.paymentTermBasis] ?? "Otra condición"} · ${c.economics.paymentTermDays ?? "—"} días` : "—"}</span>
                    <span>Importe original (sin IVA): {fmtMoney(c.economics.originalContractAmount, c.economics.currency)}</span>
                    <span>Aumentos aprobados: {fmtMoney(c.economics.totalAdjustmentIncrease, c.economics.currency)}</span>
                    <span>Reducciones aprobadas: {fmtMoney(c.economics.totalAdjustmentDecrease, c.economics.currency)}</span>
                    <span>Importe contractual vigente (sin IVA): {fmtMoney(c.economics.effectiveContractAmount, c.economics.currency)}</span>
                    {c.economics.availableAmount != null && <span>Disponible contractual: {fmtMoney(c.economics.availableAmount, c.economics.currency)}</span>}
                  </>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="instalaciones" className="space-y-3">
            {canLinkInstallations && (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setAddInstallationOpen(true)} className="min-h-[44px]">
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir instalación
                </Button>
              </div>
            )}
            <ContractInstallations contractId={contractId ?? 0} />
          </TabsContent>

          <TabsContent value="economia" className="space-y-4">
            <ContractBillingWorkspace
              contractId={contractId ?? 0}
              contextLabel={`${customerDisplay} · ${c.contractCode}`}
            />
          </TabsContent>

          {canReadCertifications && (
            <TabsContent value="certificaciones" className="space-y-3">
              <CertificationsSection contractId={contractId ?? 0} />
            </TabsContent>
          )}

          <TabsContent value="documentos">
            <ContractDocumentsPanel
              contractId={contractId ?? 0}
              canWriteCommercial={canWriteCommercial}
              canWriteEconomic={canWriteEconomic}
              canArchiveDocuments={caps?.canArchiveDocuments === true}
            />
          </TabsContent>

          <TabsContent value="partes">
            <ContractPartiesSection
              partnerRoles={c.partnerRoles ?? []}
              contractId={c.id}
              updatedAt={c.updatedAt}
              canManageParties={caps?.canManageParties === true}
              billingPartner={billToOption}
            />
          </TabsContent>
        </Tabs>
      </div>

      {canLinkInstallations && (
        <AddInstallationDialog
          contractId={contractId ?? 0}
          customerPartnerId={c.customerPartnerId}
          open={addInstallationOpen}
          createdContractLabel={createdContractContext === contractId ? `${c.contractCode} · ${c.title}` : undefined}
          onOpenChange={setAddInstallationOpen}
          onCreated={() => {
            utils.customers.contracts.listInstallations.invalidate({ contractId: contractId ?? 0 });
            query.refetch();
          }}
        />
      )}
      {canEditDraft && (
        <EditContractDialog
          contract={c}
          open={editOpen}
          onOpenChange={setEditOpen}
          canWriteEconomic={canWriteEconomic}
        />
      )}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar contrato</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="cancel-reason" className="text-xs">
              Motivo de cancelación (obligatorio, mín. 3 caracteres)
            </Label>
            <Textarea
              id="cancel-reason"
              rows={3}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder="Indica el motivo por el cual se cancela el contrato..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
              Volver
            </Button>
            <Button
              variant="destructive"
              disabled={cancellationReason.trim().length < 3 || transitionMut.isPending}
              onClick={() => handleTransition("cancelled", cancellationReason)}
            >
              {transitionMut.isPending ? "Cancelando…" : "Confirmar cancelación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
